'use client';

import { type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'next/navigation';
import { usePathname } from '@/src/i18n/navigation';

import { PanelNavigationContext } from './PanelNavigationContext';
import type { PanelEntry, PanelNavigationContextValue, PanelUrlIntent } from './PanelNavigationContext';
import { serializePanelToUrl } from './panelUrlSerializer';

function applyUrlIntent(panelId: string | null, intent: PanelUrlIntent): void {
  if (typeof window === 'undefined') return;
  if (intent === 'none') return;

  const newUrl = serializePanelToUrl(panelId);
  if (intent === 'replace') {
    window.history.replaceState(null, '', newUrl);
  } else {
    window.history.pushState(null, '', newUrl);
  }
}

/**
 * App Router sibling of PanelNavigationProvider.
 *
 * Differences from the Pages Router version:
 * - pathname-change detection uses usePathname/useSearchParams (not router.asPath)
 * - `router.beforePopState` has no App Router equivalent; we only attach a
 *   window popstate listener. This is fine for App Router HDP today — share /
 *   contact panels don't set `urlConfig`, so the Pages-Router-specific
 *   beforePopState interception isn't exercised. The raw popstate cleanup still
 *   runs for any future urlConfig panels under App Router.
 */
export function PanelNavigationProviderAppRouter({ children }: { children: ReactNode }) {
  const [stack, setStack] = useState<Array<PanelEntry>>([]);
  const pathname = usePathname() ?? '/';
  const searchParams = useSearchParams();
  const fullPath = searchParams ? `${pathname}?${searchParams.toString()}` : pathname;

  const stackRef = useRef(stack);
  stackRef.current = stack;

  const previousUrlRef = useRef<string | null>(null);
  const scrollCaptureRef = useRef(0);

  // Pathnames that *we* pushed via panel.urlConfig. usePathname() picks these
  // up from the history state change; we must not treat them as "user
  // navigated elsewhere, clear the panels" — they ARE the panel opening.
  const panelPushedPathsRef = useRef<Set<string>>(new Set());

  const previousPathRef = useRef(pathname);
  useEffect(() => {
    if (pathname !== previousPathRef.current) {
      const wasPanelPush = panelPushedPathsRef.current.has(pathname);
      previousPathRef.current = pathname;
      if (wasPanelPush) {
        panelPushedPathsRef.current.delete(pathname);
        return;
      }
      previousUrlRef.current = null;
      setStack([]);
    }
    // Intentionally only watch pathname, not fullPath — query-only navigation
    // (pagination, filters) should not clear the panel stack.
  }, [pathname]);

  // Referenced only to match the Pages Router provider's auto-clear on asPath change.
  void fullPath;

  const pushPanel = useCallback((panel: PanelEntry) => {
    if (typeof window !== 'undefined') {
      scrollCaptureRef.current = window.scrollY;
    }

    if (panel.urlConfig) {
      const hasExistingUrlConfig = stackRef.current.some((p) => p.urlConfig);

      if (typeof window !== 'undefined') {
        if (!hasExistingUrlConfig) {
          previousUrlRef.current = window.location.pathname + window.location.search + window.location.hash;
        }
        const newPathname = new URL(panel.urlConfig.url, window.location.origin).pathname;
        panelPushedPathsRef.current.add(newPathname);
        window.history.pushState(null, '', panel.urlConfig.url);
      }

      setStack((prev) => [...prev, panel]);
    } else {
      setStack((prev) => [...prev, panel]);
      applyUrlIntent(panel.id, panel.urlIntent ?? 'none');
    }
  }, []);

  const popPanel = useCallback(() => {
    const current = stackRef.current;
    if (current.length === 0) return;

    const popped = current[current.length - 1];
    setStack((prev) => prev.slice(0, -1));

    if (popped.urlConfig) {
      const remaining = current.slice(0, -1);
      const hasRemainingUrlConfig = remaining.some((p) => p.urlConfig);
      if (!hasRemainingUrlConfig && previousUrlRef.current) {
        const restoredPathname = new URL(previousUrlRef.current, window.location.origin).pathname;
        panelPushedPathsRef.current.add(restoredPathname);
        window.history.replaceState(null, '', previousUrlRef.current);
        previousUrlRef.current = null;
      }
    } else {
      const poppedIntent = popped.urlIntent ?? 'none';
      if (poppedIntent !== 'none') {
        const remaining = current.slice(0, -1);
        const nextUrlPanel = remaining.findLast((p) => (p.urlIntent ?? 'none') !== 'none');
        applyUrlIntent(nextUrlPanel?.id ?? null, 'replace');
      }
    }
  }, []);

  const clearPanels = useCallback(() => {
    const current = stackRef.current;
    const hadUrlConfigPanel = current.some((p) => p.urlConfig);
    const hadUrlIntentPanel = current.some((p) => (p.urlIntent ?? 'none') !== 'none');

    stackRef.current = [];
    setStack([]);

    if (hadUrlConfigPanel && previousUrlRef.current) {
      const restoredPathname = new URL(previousUrlRef.current, window.location.origin).pathname;
      panelPushedPathsRef.current.add(restoredPathname);
      window.history.replaceState(null, '', previousUrlRef.current);
      previousUrlRef.current = null;
    } else if (hadUrlIntentPanel) {
      applyUrlIntent(null, 'replace');
    }
  }, []);

  const hasUrlConfigPanel = stack.some((p) => p.urlConfig);

  useEffect(() => {
    if (!hasUrlConfigPanel) return;

    const handlePopState = () => {
      const current = stackRef.current;

      let lastUrlConfigIdx = -1;
      for (let i = current.length - 1; i >= 0; i--) {
        if (current[i].urlConfig) {
          lastUrlConfigIdx = i;
          break;
        }
      }

      if (lastUrlConfigIdx === -1) {
        previousUrlRef.current = null;
        stackRef.current = [];
        setStack([]);
        return;
      }

      const newStack = current.slice(0, lastUrlConfigIdx);
      stackRef.current = newStack;
      setStack(newStack);

      if (!newStack.some((p) => p.urlConfig)) {
        previousUrlRef.current = null;
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [hasUrlConfigPanel]);

  useEffect(() => {
    if (!hasUrlConfigPanel) return;

    const prev = window.history.scrollRestoration;
    window.history.scrollRestoration = 'manual';

    return () => {
      window.history.scrollRestoration = prev;
    };
  }, [hasUrlConfigPanel]);

  const value = useMemo<PanelNavigationContextValue>(
    () => ({
      stack,
      activePanel: stack.length > 0 ? stack[stack.length - 1] : null,
      hasPanels: stack.length > 0,
      pushPanel,
      popPanel,
      clearPanels,
      scrollCaptureRef,
    }),
    [stack, pushPanel, popPanel, clearPanels],
  );

  return <PanelNavigationContext.Provider value={value}>{children}</PanelNavigationContext.Provider>;
}
